import moment from 'moment'
import { Link } from 'react-router-dom'

import { Box } from 'app/components/design-system-components'
import TableComponent from 'app/components/design-system-components/Table'
import { Text } from 'app/components/design-system-components/typography'
import { activityTypeLabel } from 'app/dashboard/team_activity/FinancialInsightsReporting/helper'
import type { FIActivityRow } from 'app/dashboard/team_activity/FinancialInsightsReporting/helper'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import { pdfStandardDisplayTimeFormat, urlTo } from 'app/sm/helpers'

function NotAvailable() {
  return (
    <Text
      as="span"
      color={theme.colors.gray400}
      fontSize={theme.fontSizes.pRegular14}
    >
      N/A
    </Text>
  )
}

export interface FinancialInsightsTableProps {
  rows: FIActivityRow[]
}

const colDefs = [
  {
    accessor: 'created_at',
    Header: 'Date',
    Cell: ({ value: createdAt }: { value: string }) => {
      if (!createdAt) return <NotAvailable />
      return (
        <Text as="div">
          {moment(createdAt).format(pdfStandardDisplayTimeFormat)}
        </Text>
      )
    },
  },
  {
    accessor: 'renter',
    Header: 'Renter',
    Cell: ({
      value: renter,
      row: { original: row },
    }: {
      value: FIActivityRow['renter']
      row: { original: FIActivityRow }
    }) => {
      if (!renter) return <NotAvailable />
      const applicationId = row.application?.id
      const content = (
        <Box mb={1}>
          <Text as="div">{renter.name}</Text>
          <Text as="div">{renter.email}</Text>
        </Box>
      )
      if (!applicationId) return content
      return (
        <Link to={urlTo('progressApplication', { applicationId })}>
          {content}
        </Link>
      )
    },
  },
  {
    accessor: 'action_label',
    Header: 'Event',
    Cell: ({
      value: actionLabel,
      row: { original: row },
    }: {
      value: string
      row: { original: FIActivityRow }
    }) => {
      const label = actionLabel || activityTypeLabel[row.activity_type]
      if (!label) return <NotAvailable />
      return <Text as="span">{label}</Text>
    },
  },
  {
    accessor: 'actor',
    Header: 'Actor',
    Cell: ({ value: actor }: { value: FIActivityRow['actor'] }) => {
      if (!actor) return <NotAvailable />
      return <Text as="span">{actor.name}</Text>
    },
  },
  {
    accessor: 'application',
    Header: 'Application',
    Cell: ({ value: application }: { value: FIActivityRow['application'] }) => {
      if (!application) return <NotAvailable />
      return <Text as="span">{application.address}</Text>
    },
  },
]

export function FinancialInsightsTable({ rows }: FinancialInsightsTableProps) {
  if (rows.length === 0) {
    return (
      <Box
        textAlign="center"
        lineHeight={1.4}
        m="auto"
        mt={8}
        width={['80%', '80%', '30%']}
      >
        <Text as="div">No Financial Insights activity in this range.</Text>
      </Box>
    )
  }

  return (
    <Box width="100%" overflowX="auto">
      <TableComponent
        columns={colDefs}
        headerBgColor={theme.colors.white}
        dataToBeEntered={rows}
        textColor={theme.colors.gray600}
        showHeaderBottomBorder
        showRowBottomBorder
        thPadding={`${theme.space[5]}px`}
        fontSize={theme.fontSizes.pRegular14}
        verticalAlignTop
      />
    </Box>
  )
}
